/**
 * Auto Q&A — die Übersicht (Paket 19, Abschnitt B).
 *
 * Sie beantwortet eine Frage: **Läuft die automatische Prüfung, und hält uns
 * etwas auf?** Deshalb steht das Aufhalterband über der Liste und nicht unter
 * ihr, und deshalb ist die Reihenfolge Dringlichkeit, nicht Datenstruktur.
 *
 * Bis Stufe 5 las diese Seite Durchsichtläufe und Regeln zusammen und nannte
 * das Ergebnis „Aufträge". Jetzt gibt es Aufträge wirklich, und die Seite liest
 * `autoQa.*`.
 *
 * ── Nachfragen ────────────────────────────────────────────────────────────
 *
 * Das Haus hat keine WebSockets, keine Queue, kein Redis (B7). Fortschritt
 * kommt über TanStack Query — aber nur, solange ein Auftrag offen ist. Eine
 * ruhige Seite fragt nicht. Nach vier Fehlversuchen hält das Abfragen an und
 * sagt es: Stilles Weiterpollen gegen einen toten Server ist eine Lüge in Form
 * einer Ladeanzeige.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Seitenkopf } from "@/components/entwurf/Seitenkopf";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Kennzahl } from "@/components/entwurf/Kennzahl";
import { Zahlenband, Nebenwert } from "@/components/entwurf/Zahlenband";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { Schalter } from "@/components/entwurf/Schalter";
import { Zustandsmarke } from "@/components/entwurf/Zustandsmarke";
import { Kettenanzeige } from "@/components/entwurf/Kettenanzeige";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Select";
import { api } from "@/lib/api";
import { useProjekte } from "@/lib/haken";
import { cx, focusRing } from "@/lib/utils";
import {
  EINTRAEGE_OPTIONEN,
  ZUSTANDSFILTER,
  ZEITRAEUME,
  ZEITRAUM_LABEL,
  dauerText,
  filterAusSuche,
  median,
  seitAus,
  sucheAusFilter,
  zustaendeAusZustandsfilter,
  zustandsfilterAusZustaenden,
  type AutoQaFilter,
} from "@/lib/auto-qa-filter";
import {
  auftragsFarbe,
  auftragsMarke,
  beruhigungText,
  schrittErgebnisText,
  standAusSchritt,
  vorZeit,
} from "@/lib/auto-qa-zustand";
import {
  schrittAnzeigename,
  zielKennungAusSchnappschuss,
  zielTitelAusSchnappschuss,
} from "@/lib/auto-qa-anzeige";

/** Ab hier gilt ein Auftrag als offen — dann und nur dann wird nachgefragt. */
const OFFEN = new Set(["gebremst", "wartet", "laeuft"]);

export function AutoQaSeite() {
  const suche = useSearch({ from: "/_app/auto-qa" });
  const navigate = useNavigate();
  const projekte = useProjekte();
  const [automationenOffen, setzeAutomationenOffen] = useState(false);

  const filter = useMemo(() => filterAusSuche(suche), [suche]);
  const zustandsfilter = zustandsfilterAusZustaenden(filter.zustand);

  function setzeFilter(teil: Partial<AutoQaFilter>): void {
    void navigate({
      to: "/auto-qa",
      search: sucheAusFilter({ ...filter, ...teil }),
    });
  }

  const seit = useMemo(
    () => seitAus(filter.zeitraum),
    [filter.zeitraum],
  );

  const auftraege = useQuery({
    queryKey: ["cubic", "auto-qa", "auftraege", filter, seit.toDateString()],
    queryFn: () =>
      api.autoQa.auftraege.query({
        ...(filter.projektId ? { projektId: filter.projektId } : {}),
        ...(filter.automationId ? { automationId: filter.automationId } : {}),
        zustand: filter.zustand,
        seit,
        grenze: filter.grenze,
      }),
    /*
     * Nur solange etwas offen ist. `refetchInterval` bekommt eine Funktion,
     * damit die Entscheidung nach jedem Lauf neu fällt: Ist der letzte Auftrag
     * durch, hört die Seite von selbst auf zu fragen.
     */
    refetchInterval: (abfrage) => {
      if (abfrage.state.fetchFailureCount >= 4) return false;
      const daten = abfrage.state.data;
      return daten?.some((a) => OFFEN.has(a.zustand)) ? 5_000 : false;
    },
    refetchIntervalInBackground: false,
  });

  const aufhalter = useQuery({
    queryKey: ["cubic", "auto-qa", "aufhalter", filter.projektId],
    queryFn: () =>
      api.autoQa.aufhalter.query({
        ...(filter.projektId ? { projektId: filter.projektId } : {}),
        grenze: 20,
      }),
  });

  const automationen = useQuery({
    queryKey: ["cubic", "auto-qa", "automationen", filter.projektId],
    queryFn: async () => {
      const ids = filter.projektId
        ? [filter.projektId]
        : (projekte.data ?? []).map((p) => p.id);
      const listen = await Promise.all(
        ids.map((projektId) => api.autoQa.automationen.query({ projektId })),
      );
      return listen.flat();
    },
    enabled: (projekte.data?.length ?? 0) > 0,
  });

  const alle = auftraege.data ?? [];
  const gezeigt = filter.nurAufhalter
    ? alle.filter((a) => a.aufhalter > 0)
    : alle;

  const laufend = alle.filter((a) => OFFEN.has(a.zustand)).length;
  const aufhalterZahl = aufhalter.data?.length ?? 0;
  const gescheitert = alle.filter((a) => a.zustand === "gescheitert").length;
  const mittlereDauer = median(
    alle
      .filter((a) => a.dauerMs !== null && !OFFEN.has(a.zustand))
      .map((a) => a.dauerMs!),
  );

  const abfrageHaengt = auftraege.failureCount >= 4;
  const projektName = (id: string) =>
    projekte.data?.find((p) => p.id === id)?.kuerzel ?? "";

  if (projekte.isLoading) {
    return <Mikrolabel grad={12}>Lade Auto Q&A …</Mikrolabel>;
  }

  return (
    <div>
      <Seitenkopf
        kontext={
          filter.projektId
            ? projektName(filter.projektId)
            : `${projekte.data?.length ?? 0} Projekte sichtbar`
        }
      >
        Auto Q&A
      </Seitenkopf>
      <p className="mt-3 max-w-[62ch] text-[15.5px] font-light leading-[1.7] text-tinte2">
        Prüft Änderungen automatisch gegen die Rezepte des Projekts, bevor ein
        Mensch sie ansieht.
      </p>

      {abfrageHaengt ? (
        <div className="mt-6 border-l-2 border-warn bg-warnflaeche px-3.5 py-2.5">
          <Mikrolabel grad={12}>
            Aktualisierung angehalten — der Server antwortet nicht.
          </Mikrolabel>
          <button
            type="button"
            onClick={() => void auftraege.refetch()}
            className={cx(
              "mt-1.5 text-[12px] text-akzent underline decoration-1 underline-offset-[3px]",
              focusRing,
            )}
          >
            Erneut versuchen
          </button>
        </div>
      ) : null}

      {/* B2 — vier Werte, die Nullregel steckt in `Kennzahl`. */}
      <Zahlenband
        className="mt-8"
        neben={
          <>
            <Nebenwert
              wert={gescheitert}
              label={`gescheitert (${ZEITRAUM_LABEL[filter.zeitraum]})`}
            />
            <Nebenwert wert={dauerText(mittlereDauer)} label="Dauer im Mittel" />
            <Nebenwert
              wert={(automationen.data ?? []).filter((a) => a.zustand === "aktiv").length}
              label="Automationen scharf"
            />
          </>
        }
      >
        <Kennzahl label="laufend" grad={56} wert={laufend} ton="akzent" />
        <Kennzahl label="Aufhalter" grad={56} wert={aufhalterZahl} ton="warn" />
      </Zahlenband>

      {/* B6 — eine Zeile, keine Filterleiste mit eigenem Rahmen. */}
      <div className="mt-7 flex flex-wrap items-center gap-3">
        <Select
          value={filter.projektId ?? "alle"}
          onValueChange={(wert) =>
            setzeFilter({ projektId: wert === "alle" ? undefined : wert })
          }
        >
          <SelectTrigger className="w-[190px]" aria-label="Projekt">
            <SelectValue placeholder="Alle Projekte" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Projekte</SelectItem>
            {(projekte.data ?? []).map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.kuerzel} · {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filter.automationId ?? "alle"}
          onValueChange={(wert) =>
            setzeFilter({ automationId: wert === "alle" ? undefined : wert })
          }
        >
          <SelectTrigger className="w-[190px]" aria-label="Automation">
            <SelectValue placeholder="Alle Automationen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Automationen</SelectItem>
            {(automationen.data ?? []).map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={zustandsfilter}
          onValueChange={(wert) =>
            setzeFilter({ zustand: zustaendeAusZustandsfilter(wert) })
          }
        >
          <SelectTrigger className="w-[170px]" aria-label="Auftragsstatus">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {zustandsfilter === "eigene" ? (
              <SelectItem value="eigene">Eigene Auswahl</SelectItem>
            ) : null}
            {ZUSTANDSFILTER.map((option) => (
              <SelectItem key={option.wert} value={option.wert}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filter.zeitraum}
          onValueChange={(wert) =>
            setzeFilter({ zeitraum: wert as AutoQaFilter["zeitraum"] })
          }
        >
          <SelectTrigger className="w-[130px]" aria-label="Zeitraum">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ZEITRAEUME.map((z) => (
              <SelectItem key={z} value={z}>
                {ZEITRAUM_LABEL[z]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={String(filter.grenze)}
          onValueChange={(wert) =>
            setzeFilter({
              grenze: Number(wert) as AutoQaFilter["grenze"],
            })
          }
        >
          <SelectTrigger
            className="w-[145px]"
            aria-label="Anzahl der Aufträge"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EINTRAEGE_OPTIONEN.map((anzahl) => (
              <SelectItem key={anzahl} value={String(anzahl)}>
                {anzahl} Einträge
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2.5">
          <Mikrolabel grad={12}>Nur Aufhalter</Mikrolabel>
          <Schalter
            an={filter.nurAufhalter}
            onKlick={() => setzeFilter({ nurAufhalter: !filter.nurAufhalter })}
            ariaLabel="Nur Aufträge mit Aufhaltern zeigen"
          />
        </div>
      </div>

      {/* B5 — nur wenn es Aufhalter gibt. Sonst verschwindet es ersatzlos. */}
      {aufhalterZahl > 0 ? (
        <div className="mt-7 bg-warnflaeche px-4 py-3.5">
          <Mikrolabel grad={12} ton="laut" betont>
            {aufhalterZahl === 1 ? "1 Aufhalter" : `${aufhalterZahl} Aufhalter`}
          </Mikrolabel>
          <div className="mt-2.5">
            {(aufhalter.data ?? []).slice(0, 5).map((a) => (
              <Link
                key={a.befundId}
                to="/auto-qa/auftraege/$auftragId"
                params={{ auftragId: a.auftragId }}
                hash={a.befundId}
                className={cx(
                  "flex items-baseline gap-3 py-1.5 hover:bg-wisch",
                  focusRing,
                )}
              >
                <span className="flex-none font-mono text-[11px] uppercase text-warn">
                  {a.schwere}
                </span>
                <span className="flex-none font-mono text-[11.5px] tabular-nums text-tinte3">
                  {a.datei}
                  {a.zeile === null ? "" : `:${a.zeile}`}
                </span>
                <span className="min-w-0 flex-1 truncate text-[13px] text-tinte">
                  {a.text}
                </span>
              </Link>
            ))}
            {aufhalterZahl > 5 ? (
              <Mikrolabel grad={11.5} className="mt-1.5 block">
                … und {aufhalterZahl - 5} weitere
              </Mikrolabel>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* B3 — eine Zeile je Auftrag, keine Karten. */}
      <div className="mt-8">
        <Rubrik
          anzahl={
            gezeigt.length === alle.length
              ? `${alle.length} Aufträge`
              : `${gezeigt.length} von ${alle.length}`
          }
        >
          Aufträge
        </Rubrik>

        {auftraege.isLoading ? (
          <SkelettZeilen />
        ) : gezeigt.length === 0 ? (
          <Leerzeile
            text={
              (automationen.data ?? []).length === 0
                ? "Noch keine Automation. Eine Automation startet Rezepte, sobald etwas geschieht — zum Beispiel eine Sicherheitsprüfung bei jedem neuen Merge Request."
                : alle.length === 0
                  ? "Noch nichts gelaufen. Der nächste Anlass startet die erste Prüfung."
                  : "Kein Auftrag passt zu diesem Filter."
            }
            aktion={
              (automationen.data ?? []).length === 0 ? (
                <Link
                  to="/auto-qa/automationen/$automationId"
                  params={{ automationId: "neu" }}
                  className={cx("text-[12.5px] text-akzent underline decoration-1 underline-offset-[3px]", focusRing)}
                >
                  Automation anlegen
                </Link>
              ) : alle.length > 0 ? (
                <button
                  type="button"
                  onClick={() =>
                    void navigate({ to: "/auto-qa", search: {} })
                  }
                  className={cx("text-[12.5px] text-akzent underline decoration-1 underline-offset-[3px]", focusRing)}
                >
                  Filter zurücksetzen
                </button>
              ) : null
            }
          />
        ) : (
          gezeigt.map((auftrag) => {
            const gelaufen = auftrag.schritte.filter(
              (s) => s.zustand !== "ausstehend",
            ).length;
            const laufenderSchritt = auftrag.schritte.find(
              (s) => s.zustand === "laeuft",
            );
            const zielKennung = zielKennungAusSchnappschuss(
              auftrag.zielSchnappschuss,
            );
            const zielTitel = zielTitelAusSchnappschuss(
              auftrag.zielSchnappschuss,
            );
            return (
              <Link
                key={auftrag.id}
                to="/auto-qa/auftraege/$auftragId"
                params={{ auftragId: auftrag.id }}
                className={cx(
                  "flex items-center gap-3.5 border-b border-linie py-3 hover:bg-wisch",
                  focusRing,
                )}
              >
                <Zustandsmarke farbe={auftragsFarbe(auftrag.zustand)}>
                  {auftragsMarke(auftrag.zustand)}
                </Zustandsmarke>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] text-tinte">
                    <span className="font-mono text-[12px] text-tinte4">
                      {zielKennung ??
                        (auftrag.zielArt === "merge_request"
                          ? "MR"
                          : auftrag.zielArt)}
                    </span>{" "}
                    {zielTitel ??
                      (automationen.data ?? []).find(
                        (a) => a.id === auftrag.automationId,
                      )?.name ??
                      "Automation entfernt"}
                    {auftrag.probelauf ? " · Probelauf" : ""}
                  </span>
                  <span className="mt-0.5 block text-[11.5px] text-tinte4">
                    {auftrag.zustand === "gebremst"
                      ? beruhigungText(auftrag.gebremstBis)
                      : auftrag.zustand === "laeuft" && laufenderSchritt
                        ? `Schritt ${laufenderSchritt.reihenfolge + 1} von ${auftrag.schritte.length}`
                        : auftrag.zustand === "gescheitert" && auftrag.abbruchgrund
                          ? auftrag.abbruchgrund
                          : `${gelaufen} von ${auftrag.schritte.length} Schritten · ${dauerText(auftrag.dauerMs)}`}
                  </span>
                </span>

                <Kettenanzeige
                  glieder={auftrag.schritte.map((s) => ({
                    name: schrittAnzeigename(s),
                    stand: standAusSchritt(s.zustand, s.ergebnis),
                    ergebnis: schrittErgebnisText(s.zustand, s.ergebnis),
                  }))}
                />

                {auftrag.aufhalter > 0 ? (
                  <span className="flex-none text-[12px] tabular-nums text-warn">
                    {auftrag.aufhalter} Aufhalter
                  </span>
                ) : null}

                <Mikrolabel
                  grad={11}
                  className="flex-none"
                  title={new Date(auftrag.angelegtAm).toLocaleString("de-DE")}
                >
                  {vorZeit(auftrag.angelegtAm)}
                </Mikrolabel>
              </Link>
            );
          })
        )}
      </div>

      {/* Ausklappbar am Fuß: welche Vorschriften sind scharf. */}
      <div className="mt-10">
        <Rubrik
          anzahl={`${(automationen.data ?? []).length}`}
          aktion={
            <Link
              to="/auto-qa/automationen/$automationId"
              params={{ automationId: "neu" }}
              className={cx("text-[12px] text-akzent underline decoration-1 underline-offset-[3px]", focusRing)}
            >
              Automation anlegen
            </Link>
          }
        >
          <button
            type="button"
            aria-expanded={automationenOffen}
            onClick={() => setzeAutomationenOffen((x) => !x)}
            className={cx("cursor-pointer", focusRing)}
          >
            Automationen {automationenOffen ? "▾" : "▸"}
          </button>
        </Rubrik>

        {automationenOffen
          ? (automationen.data ?? []).map((a) => (
              <Link
                key={a.id}
                to="/auto-qa/automationen/$automationId"
                params={{ automationId: a.id }}
                className={cx(
                  "flex items-center gap-3.5 border-b border-linie py-3 hover:bg-wisch",
                  focusRing,
                )}
              >
                <Zustandsmarke
                  farbe={a.zustand === "aktiv" ? "blau" : "grau"}
                >
                  {a.zustand === "aktiv"
                    ? "Aktiv"
                    : a.zustand === "pausiert"
                      ? "Pausiert"
                      : "Entwurf"}
                </Zustandsmarke>
                <span className="min-w-0 flex-1 truncate text-[13.5px] text-tinte">
                  {a.name}
                </span>
                <Mikrolabel grad={11} className="flex-none">
                  {a.anlass}
                </Mikrolabel>
                <Mikrolabel grad={11} className="flex-none">
                  {a.schritte.length}{" "}
                  {a.schritte.length === 1 ? "Schritt" : "Schritte"}
                </Mikrolabel>
              </Link>
            ))
          : null}
      </div>
    </div>
  );
}

/**
 * Skelettzeilen in der Höhe echter Zeilen (B7).
 *
 * Kein Spinner mitten im Inhalt: Die Liste darf beim Nachladen nicht unter dem
 * Zeiger wegspringen.
 */
function SkelettZeilen() {
  return (
    <div aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3.5 border-b border-linie py-3"
        >
          <div className="h-[26px] w-[84px] flex-none bg-wisch" />
          <div className="h-[13px] min-w-0 flex-1 bg-wisch" />
          <div className="h-[7px] w-[52px] flex-none bg-wisch" />
        </div>
      ))}
    </div>
  );
}

/** Leerzustand als getönte Fläche, nie mit gestricheltem Rahmen (B8). */
function Leerzeile({
  text,
  aktion,
}: {
  text: string;
  aktion?: React.ReactNode;
}) {
  return (
    <div className="border-b border-linie bg-wisch px-4 py-6">
      <p className="max-w-[62ch] text-[13.5px] font-light leading-[1.65] text-tinte3">
        {text}
      </p>
      {aktion ? <div className="mt-3">{aktion}</div> : null}
    </div>
  );
}
